import React, { useState } from 'react';
import useGameStore from '../../store/gameStore';
import { formatMoney } from '../../utils/format';
import {
  TAX_REGIMES, TAX_OPTIMIZATIONS, TVA_PAYMENT_BASES,
  TVA_QUARTERLY_OPTION_THRESHOLD,
} from '../../data/taxes';
import {
  Settings2, Percent, ChevronDown, ChevronUp, CheckCircle2,
  AlertTriangle, ToggleLeft, ToggleRight, Receipt, CalendarClock,
} from 'lucide-react';

/**
 * Fiscalité — onglet Régime fiscal + Optimisations. Permet de basculer
 * entre les régimes (auto-entrepreneur, simplifié, normal) et d'activer
 * des optimisations (suramortissement, IR/IS, etc.). Extracted from
 * Fiscalite.jsx on 28/05/2026.
 */
export default function RegimeTab() {
  const {
    taxRegime, setTaxRegime, activeOptimizations, toggleOptimization,
    tvaPaymentBasis, setTvaPaymentBasis, tvaQuarterlyOption, setTvaQuarterlyOption,
    tvaDeferredCollected,
  } = useGameStore();
  const [showRegimeDetails, setShowRegimeDetails] = useState(null);
  const currentBasis = tvaPaymentBasis || 'encaissement';

  return (
    <div className="space-y-4">
      {/* Regime selection */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <Settings2 className="w-4 h-4" /> Régime fiscal
        </h3>
        <div className="space-y-3">
          {Object.values(TAX_REGIMES).map(r => {
            const isActive = taxRegime === r.id;
            return (
              <div key={r.id}>
                <div
                  onClick={() => setTaxRegime(r.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setTaxRegime(r.id); }}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all cursor-pointer ${
                    isActive ? 'border-accent bg-accent/10' : 'border-ink-600 hover:border-ink-500'
                  }`}
                  data-testid={`regime-${r.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className={`font-semibold text-sm ${isActive ? 'text-foreground' : 'text-slate-100'}`}>{r.name}</p>
                    {isActive && <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent text-white">Actif</span>}
                  </div>
                  <p className="text-xs text-slate-500">{r.description}</p>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowRegimeDetails(showRegimeDetails === r.id ? null : r.id); }}
                    className="text-[10px] text-foreground mt-2 flex items-center gap-0.5 hover:underline"
                  >
                    {showRegimeDetails === r.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    Détails
                  </button>
                </div>
                {showRegimeDetails === r.id && (
                  <div className="mt-1 ml-4 text-xs space-y-2 p-3 bg-ink-700/30 rounded-lg">
                    <div>
                      <p className="font-medium text-money mb-1">Avantages :</p>
                      {r.advantages.map((a) => (
                        <p key={a} className="text-money flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {a}</p>
                      ))}
                    </div>
                    <div>
                      <p className="font-medium text-danger mb-1">Inconvénients :</p>
                      {r.disadvantages.map((d) => (
                        <p key={d} className="text-danger flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {d}</p>
                      ))}
                    </div>
                    {r.accountingCost && (
                      <p className="text-slate-500">Coût comptable : <strong>{formatMoney(r.accountingCost)}/mois</strong></p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 13/06/2026 — Options de TVA (exigibilité + rythme) */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4" data-testid="tva-options-card">
        <h3 className="text-sm font-semibold text-slate-200 mb-1 flex items-center gap-2">
          <Receipt className="w-4 h-4" /> Options de TVA
        </h3>
        <p className="text-[11px] text-slate-500 mb-3">
          Exigibilité et rythme de déclaration, conformes à la réglementation française des
          prestations de services (transport).
        </p>

        {/* Exigibilité : encaissement / débits */}
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1.5">Exigibilité</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          {Object.values(TVA_PAYMENT_BASES).map(b => {
            const isActive = currentBasis === b.id;
            return (
              <div
                key={b.id}
                onClick={() => setTvaPaymentBasis(b.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setTvaPaymentBasis(b.id); }}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  isActive ? 'border-accent bg-accent/10' : 'border-ink-600 hover:border-ink-500'
                }`}
                data-testid={`tva-basis-${b.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-semibold text-slate-100">{b.name}</p>
                  {isActive && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent text-white">Actif</span>}
                </div>
                <p className="text-[10px] text-slate-500 font-mono uppercase tracking-wider mb-1">{b.badge}</p>
                <p className="text-[10px] text-slate-400 leading-snug">{b.description}</p>
                <p className="text-[10px] text-money mt-1">✓ {b.pro}</p>
                <p className="text-[10px] text-warn mt-0.5">✗ {b.con}</p>
              </div>
            );
          })}
        </div>
        {currentBasis === 'encaissement' && (tvaDeferredCollected || 0) > 0 && (
          <p className="text-[10px] text-slate-500 mb-2" data-testid="tva-deferred-info">
            TVA collectée différée (exigible à la prochaine déclaration) :{' '}
            <strong className="text-slate-300">{formatMoney(tvaDeferredCollected)}</strong>
          </p>
        )}

        {/* Rythme : option trimestrielle au réel normal */}
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1.5 mt-3 flex items-center gap-1">
          <CalendarClock className="w-3 h-3" /> Rythme de déclaration
        </p>
        {taxRegime === 'reel_normal' ? (
          <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-ink-600">
            <div>
              <p className="text-xs font-medium text-slate-200">Option trimestrielle (réel normal)</p>
              <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                Au réel normal, la déclaration est mensuelle. L&apos;administration tolère un rythme
                trimestriel tant que la TVA exigible annuelle reste sous{' '}
                {TVA_QUARTERLY_OPTION_THRESHOLD.toLocaleString('fr-FR')} € (CGI, ann. II, art. 39 bis).
                En cas de dépassement, retour automatique au mensuel.
              </p>
            </div>
            <button
              onClick={() => setTvaQuarterlyOption(!tvaQuarterlyOption)}
              className="flex-shrink-0"
              data-testid="tva-quarterly-option-toggle"
              aria-label="Option trimestrielle"
            >
              {tvaQuarterlyOption
                ? <ToggleRight className="w-7 h-7 text-money" />
                : <ToggleLeft className="w-7 h-7 text-slate-500" />}
            </button>
          </div>
        ) : (
          <p className="text-[10px] text-slate-500 p-3 rounded-lg border border-ink-600 bg-ink-700/20" data-testid="tva-rhythm-simplifie-note">
            Régime réel simplifié : déclaration <strong>trimestrielle</strong> imposée par le régime.
            Passez au réel normal pour un rythme mensuel (crédit de TVA remboursé plus vite).
          </p>
        )}
      </div>

      {/* Tax Optimizations */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <Percent className="w-4 h-4" /> Optimisation fiscale
        </h3>
        <div className="space-y-3">
          {Object.values(TAX_OPTIMIZATIONS).map(opt => {
            const isActive = !!activeOptimizations[opt.id];
            const canActivate = !opt.requiresRegime || taxRegime === opt.requiresRegime;
            return (
              <div
                key={opt.id}
                className={`p-3 rounded-lg border transition-colors ${
                  isActive ? 'border-money/60 bg-money/10' :
                  !canActivate ? 'border-ink-600 bg-ink-700/30 opacity-60' :
                  'border-ink-600 hover:border-ink-500'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-200">{opt.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{opt.description}</p>
                    <p className="text-[10px] text-money mt-1">{opt.effect}</p>
                    {opt.requiresRegime && !canActivate && (
                      <p className="text-[10px] text-danger mt-0.5">Requiert : {TAX_REGIMES[opt.requiresRegime]?.name}</p>
                    )}
                    {opt.drawback && (
                      <details className="mt-1.5 cursor-pointer group">
                        <summary className="text-[10px] text-warn font-medium flex items-center gap-1 list-none">
                          <AlertTriangle className="w-3 h-3" />
                          Inconvénients
                          <span className="text-slate-500 group-open:hidden">▾</span>
                          <span className="text-slate-500 hidden group-open:inline">▴</span>
                        </summary>
                        <p className="text-[10px] text-warn/80 mt-1 leading-relaxed pl-4 italic" data-testid={`opt-drawback-${opt.id}`}>
                          {opt.drawback}
                        </p>
                      </details>
                    )}
                  </div>
                  <button
                    onClick={() => toggleOptimization(opt.id)}
                    disabled={!canActivate}
                    className="ml-3 flex-shrink-0"
                    data-testid={`toggle-opt-${opt.id}`}
                  >
                    {isActive ? (
                      <ToggleRight className="w-8 h-8 text-money" />
                    ) : (
                      <ToggleLeft className={`w-8 h-8 ${canActivate ? 'text-slate-500' : 'text-slate-600'}`} />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
